import React, { useEffect, useState } from 'react';
import { useTable, useFilters, useGlobalFilter, useSortBy, useAsyncDebounce } from 'react-table';
import { matchSorter } from 'match-sorter';
import { Table, Spinner, Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ReactSortable } from 'react-sortablejs';
import moment from 'moment';

function DefaultColumnFilter({
  column: { id, Header, filterType, filterName },
  filterValue,
  onFilterChange
}) {

  const [value, setValue] = useState(filterValue || '')

  useEffect(() => {
    setValue(filterValue || "");
  }, [filterValue]);

  const onChangeDebounced = useAsyncDebounce((val) => {
    onFilterChange(filterName, val)
  }, 500) // espera 500ms

  const handleChange = (e) => {
    console.log(e.target.value)
    const val = filterType === "date" && e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : e.target.value
    setValue(val)
    onChangeDebounced(val)
  }

  return (
    <div className="mt-3">
      {filterType === "text" ? (
        <input
          className="form-control form-control-sm rounded-pill"
          value={value}
          onChange={handleChange}
          placeholder={`Filtrar por ${Header}`}
          onClick={(e) => e.stopPropagation()}
        />
      ) : filterType === "date" ? (
        <input
          className="form-control form-control-sm rounded-pill"
          value={value}
          onChange={handleChange}
          placeholder={`Filtrar por ${Header}`}
          type='date'
          onClick={(e) => e.stopPropagation()}
        />

      ): null}

    </div>
  )
}
function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

fuzzyTextFilterFn.autoRemove = val => !val

const TableBody = ({ getTableBodyProps, rows, prepareRow, maySortableRows, handleAddFunc, handleSortFunc }) => {
  const [sortableState, setSortableState] = React.useState(rows);
  React.useEffect(() => {
    const _rows = rows.sort((a, b) => a.original.secuencia - b.original.secuencia)
    setSortableState(_rows);
  }, [rows, maySortableRows]);
  const onSort = () => {
    if (handleSortFunc) {
      handleSortFunc(sortableState);
    }
  }
  return (
    maySortableRows ? (
      <ReactSortable
        tag="tbody"
        list={sortableState}
        setList={setSortableState}
        {...getTableBodyProps()}
        onSort={(_, sortable, evt) => onSort()}>
        {sortableState.map((row, i) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()} onClick={() => handleAddFunc(row.original)}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}> {cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </ReactSortable>
    ) : (
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()} onClick={() => handleAddFunc(row.original)}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
    )
  )
}

function CustomTableBackendFilters({ columns, data, isLoadingData, addFunc, closeSearchModal, maySortableRows, handleSortFunc, ...props }) {
  const [filters, setFilters] = React.useState({});
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const handleFilterChange = (columnId, value) => {
    const newFilters = {
      ...filters,
      [columnId]: value,
    };

    // Eliminar campos vacíos
    Object.keys(newFilters).forEach(key => {
      if (!newFilters[key]) delete newFilters[key];
    });

    setFilters(newFilters);

    props.onFiltersChange && props.onFiltersChange(newFilters);
  };

  const handleCleanFilters = () => {
    setFilters({})
    props.onFiltersChange && props.onFiltersChange({});
  }


  const columnsWithFilters = React.useMemo(() => {
    return columns.map(col => ({
      ...col,
      Filter: (filterProps) => (
        <DefaultColumnFilter
          {...filterProps}
          onFilterChange={handleFilterChange}
          filterValue={filters[col.filterName] || ''}
        />
      )
    }));
  }, [columns, filters]);

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    /*state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,*/
  } = useTable(
    {
      columns: columnsWithFilters,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      manualFilters: true
    },
    useFilters, // useFilters!
    //useGlobalFilter, // useGlobalFilter!
    useSortBy

  )

  const handleAddFunc = (data) => {
    if (addFunc) {
      addFunc(data);
    }
    if (closeSearchModal) {
      closeSearchModal();
    }
  }

  return (
    <div className="table-loader-container">
      {
        Object.keys(filters).length !== 0 && (
          <OverlayTrigger
            placement="auto"
            overlay={<Tooltip {...props}>Limpiar Filtros</Tooltip>}
          >
            <Button onClick={handleCleanFilters} className='mb-2' size='sm' variant='outline-secondary'>
              <i className="material-icons">filter_alt_off</i>
            </Button>

          </OverlayTrigger>
        )
      }
      <div className={`table-container mb-3`}>

        <Table className="custom-table" striped>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} >
                {headerGroup.headers.map(column => (
                  <th className="text-muted font-weight-normal" {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <span className="ml-2">{column.render('Header')}</span>
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? <i className="material-icons">arrow_drop_down</i> : <i className="material-icons">arrow_drop_up</i>) : column.canFilter ? <i className="fas fa-sort ml-2"></i> : null}
                    </span>
                    <div className="mt-4">{column.canFilter ? column.render('Filter') : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <TableBody
            getTableBodyProps={getTableBodyProps}
            rows={rows}
            prepareRow={prepareRow}
            maySortableRows={maySortableRows}
            handleAddFunc={handleAddFunc}
            handleSortFunc={handleSortFunc} />
        </Table>
        {
          !isLoadingData && rows.length === 0 && (
            <Alert variant="info" className="my-4 mx-3">
              Aún no hay registros, comience creando algunos.
            </Alert>
          )
        }
      </div>
      {
        isLoadingData && (
          <div className="d-flex justify-content-center align-items-center table-loader">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        )
      }
    </div>
  )
}

// Define a custom filter filter function!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter(row => {
    const rowValue = row.values[id]
    return rowValue >= filterValue
  })
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = val => typeof val !== 'number'

export default CustomTableBackendFilters;